import React, { createContext, useContext, useState, useCallback, useRef, ReactNode, useEffect } from 'react';
import { Product, ProductSection, AnalyticsEvent } from './types';
import { salesApi } from '../../services/salesApi';
import { salesAnalytics } from './utils/analytics';

interface SalesContextType {
  // Products
  products: Product[];
  productsLoading: boolean;
  productsError: string | null;
  fetchProducts: (force?: boolean) => Promise<void>;
  createProduct: (product: Partial<Product>) => Promise<Product>;
  updateProduct: (id: number, product: Partial<Product>) => Promise<Product>;
  deleteProduct: (id: number) => Promise<void>;

  // Sections
  sections: ProductSection[];
  sectionsLoading: boolean;
  sectionsError: string | null;
  fetchSections: (force?: boolean) => Promise<void>;
  createSection: (section: Partial<ProductSection>) => Promise<ProductSection>;
  updateSection: (id: number, section: Partial<ProductSection>) => Promise<ProductSection>;
  deleteSection: (id: number) => Promise<void>;

  // Analytics
  trackEvent: (event: AnalyticsEvent) => void;
}

const SalesContext = createContext<SalesContextType | undefined>(undefined);

export const useSales = () => {
  const context = useContext(SalesContext);
  if (context === undefined) {
    throw new Error('useSales must be used within a SalesProvider');
  }
  return context;
};

interface SalesProviderProps {
  children: ReactNode;
}

export const SalesProvider: React.FC<SalesProviderProps> = ({ children }) => {
  // Products state
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const productsFetched = useRef(false);

  // Sections state
  const [sections, setSections] = useState<ProductSection[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const sectionsFetched = useRef(false);

  // Fetch products
  const fetchProducts = useCallback(async (force = false) => {
    if (!force && productsFetched.current && products.length > 0) {
      return;
    }

    setProductsLoading(true);
    setProductsError(null);

    try {
      const data = await salesApi.getProducts();
      setProducts(data);
      productsFetched.current = true;
      salesAnalytics.trackProductView(0);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Erro ao carregar produtos';
      setProductsError(errorMessage);
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  }, [products.length]);

  // Fetch sections
  const fetchSections = useCallback(async (force = false) => {
    if (!force && sectionsFetched.current && sections.length > 0) {
      return;
    }

    setSectionsLoading(true);
    setSectionsError(null);

    try {
      const data = await salesApi.getSections();
      if (Array.isArray(data)) {
        setSections(data);
      } else {
        console.error('Invalid sections data format:', data);
        setSections([]);
      }
      sectionsFetched.current = true;
    } catch (error: any) {
      // Error handling
      const errorMessage = error?.response?.data?.error || error?.message || 'Erro ao carregar secções';
      setSectionsError(errorMessage);
      console.error('Error fetching sections:', error);
      // Ensure it stays an array even on error if it was never set? 
      // Initial state is [] so fine.
    } finally {
      setSectionsLoading(false);
    }
  }, [sections.length]);

  // Create product
  const createProduct = useCallback(async (productData: Partial<Product>): Promise<Product> => {
    try {
      const newProduct = await salesApi.createProduct(productData);
      setProducts(prev => [...prev, newProduct]);
      salesAnalytics.trackProductCreate();
      return newProduct;
    } catch (error: any) {
      throw new Error(error?.response?.data?.error || error?.message || 'Erro ao criar produto');
    }
  }, []);

  // Update product
  const updateProduct = useCallback(async (id: number, productData: Partial<Product>): Promise<Product> => {
    try {
      const updatedProduct = await salesApi.updateProduct(id, productData);
      setProducts(prev => prev.map(p => p.id === id ? updatedProduct : p));
      salesAnalytics.trackProductEdit(id);
      return updatedProduct;
    } catch (error: any) {
      throw new Error(error?.response?.data?.error || error?.message || 'Erro ao atualizar produto');
    }
  }, []);

  // Delete product
  const deleteProduct = useCallback(async (id: number): Promise<void> => {
    try {
      await salesApi.deleteProduct(id);
      setProducts(prev => prev.filter(p => p.id !== id));
      salesAnalytics.trackProductDelete(id);
    } catch (error: any) {
      throw new Error(error?.response?.data?.error || error?.message || 'Erro ao eliminar produto');
    }
  }, []);

  // Create section
  const createSection = useCallback(async (sectionData: Partial<ProductSection>): Promise<ProductSection> => {
    try {
      const newSection = await salesApi.createSection(sectionData);
      setSections(prev => {
        const newSections = [...prev, newSection];
        return newSections.sort((a, b) => a.order - b.order);
      });
      return newSection;
    } catch (error: any) {
      throw new Error(error?.response?.data?.error || error?.message || 'Erro ao criar secção');
    }
  }, []);

  // Update section
  const updateSection = useCallback(async (id: number, sectionData: Partial<ProductSection>): Promise<ProductSection> => {
    try {
      const updatedSection = await salesApi.updateSection(id, sectionData);
      setSections(prev => {
        const updated = prev.map(s => s.id === id ? updatedSection : s);
        return updated.sort((a, b) => a.order - b.order);
      });
      return updatedSection;
    } catch (error: any) {
      throw new Error(error?.response?.data?.error || error?.message || 'Erro ao atualizar secção');
    }
  }, []);

  // Delete section
  const deleteSection = useCallback(async (id: number): Promise<void> => {
    try {
      await salesApi.deleteSection(id);
      setSections(prev => prev.filter(s => s.id !== id));
    } catch (error: any) {
      throw new Error(error?.response?.data?.error || error?.message || 'Erro ao eliminar secção');
    }
  }, []);

  // Track event
  const trackEvent = useCallback((event: AnalyticsEvent) => {
    salesAnalytics.track(event);
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProducts();
    fetchSections();
  }, []);

  const value: SalesContextType = {
    products,
    productsLoading,
    productsError,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    sections,
    sectionsLoading,
    sectionsError,
    fetchSections,
    createSection,
    updateSection,
    deleteSection,
    trackEvent,
  };

  return (
    <SalesContext.Provider value={value}>
      {children}
    </SalesContext.Provider>
  );
};

export default SalesContext;
